import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  ContentCategoryRow,
  ContentNearVariantGroup,
} from "../../common/contentTypes";
import * as Content from "../../services/api/Content";

/**
 * Categories are the lookup every content screen needs (filters, editor
 * selects, labels), so they load once per page and expose a reload for screens
 * that mutate them.
 */
export function useCategories(includeDeleted = false) {
  const [categories, setCategories] = useState<ContentCategoryRow[]>([]);
  const [nearVariants, setNearVariants] = useState<ContentNearVariantGroup[]>(
    [],
  );
  const [loading, setLoading] = useState(true);

  /**
   * `silent` refetches without flipping `loading`. Every row action here ends in
   * a reload, and showing the page-level spinner for those unmounted the whole
   * list and remounted it — a full-page flicker for a single row's change.
   */
  const reload = useCallback(
    async ({ silent }: { silent?: boolean } = {}) => {
      if (!silent) {
        setLoading(true);
      }
      try {
        const result = await Content.listCategories({ includeDeleted });
        setCategories(result.categories ?? []);
        setNearVariants(result.nearVariants ?? []);
      } catch (err) {
        toast.error(`Failed to load categories: ${(err as Error).message}`);
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [includeDeleted],
  );

  useEffect(() => {
    reload();
  }, [reload]);

  return { categories, nearVariants, loading, reload };
}

export function categoryLabel(
  categories: ContentCategoryRow[],
  categoryId: string,
): string {
  const match = categories.find((category) => category.id === categoryId);
  return match ? `${match.group}:${match.name}` : categoryId;
}
